import type { ClubProgress } from './types';

export const CLUB_LEVEL_MAX = 10;

export type ClubProgressMetric = keyof Omit<ClubProgress['metrics'], 'kickImprovedPlayerIds'>;

export type ClubAchievement = {
  id: string;
  title: string;
  description: string;
  metric: ClubProgressMetric | 'kickImprovedPlayers';
  target: number;
  xp: number;
};

/** The thresholds intentionally leave room for different career paths: a
 * strong league run, focused scouting or player development can all move the
 * club forward. There is no seasonal reset. */
export const CLUB_LEVEL_XP = [0, 35, 100, 190, 300, 430, 570, 720, 870, 1000] as const;

export const clubAchievements: ClubAchievement[] = [
  { id: 'first_win', title: 'Primera victoria', description: 'Gana 1 partido de liga.', metric: 'wins', target: 1, xp: 35 },
  { id: 'ten_wins', title: 'Equipo competitivo', description: 'Gana 10 partidos de liga.', metric: 'wins', target: 10, xp: 65 },
  { id: 'thirty_wins', title: 'Proyecto ganador', description: 'Gana 30 partidos de liga.', metric: 'wins', target: 30, xp: 110 },
  { id: 'shot_growth_3', title: 'Artillería en marcha', description: 'Mejora el Tiro de 3 jugadores distintos.', metric: 'kickImprovedPlayers', target: 3, xp: 60 },
  { id: 'shot_growth_8', title: 'Especialistas del disparo', description: 'Mejora el Tiro de 8 jugadores distintos.', metric: 'kickImprovedPlayers', target: 8, xp: 110 },
  { id: 'fire_scout_5', title: 'Llama de reclutamiento', description: 'Ficha 5 jugadores de Fuego.', metric: 'fireRecruits', target: 5, xp: 50 },
  { id: 'fire_scout_10', title: 'Legión de Fuego', description: 'Ficha 10 jugadores de Fuego.', metric: 'fireRecruits', target: 10, xp: 95 },
  { id: 'wind_scout_5', title: 'Corriente favorable', description: 'Ficha 5 jugadores de Viento.', metric: 'windRecruits', target: 5, xp: 50 },
  { id: 'forest_scout_5', title: 'Raíces del proyecto', description: 'Ficha 5 jugadores de Bosque.', metric: 'forestRecruits', target: 5, xp: 50 },
  { id: 'mountain_scout_5', title: 'Base de Montaña', description: 'Ficha 5 jugadores de Montaña.', metric: 'mountainRecruits', target: 5, xp: 50 },
  { id: 'squad_depth_20', title: 'Plantilla profunda', description: 'Completa 20 fichajes.', metric: 'recruits', target: 20, xp: 80 },
  { id: 'league_title', title: 'Campeón de liga', description: 'Gana una liga.', metric: 'leagueTitles', target: 1, xp: 170 },
  { id: 'tournament_title', title: 'Conquistador continental', description: 'Gana un torneo especial.', metric: 'tournamentTitles', target: 1, xp: 200 },
];

type Reward = Record<string, number>;

/** Rewards are granted automatically when reaching each level. The most
 * flexible and valuable tickets deliberately appear only in upper levels. */
export const clubLevelRewards: Record<number, Reward> = {
  2: { token_youth: 2 },
  3: { token_fire: 1, token_wind: 1, token_forest: 1, token_mountain: 1 },
  4: { token_youth: 3, token_technique_choice: 1 },
  5: { token_fire: 2, token_wind: 2, token_forest: 2, token_mountain: 2 },
  6: { token_master_choice: 1, token_technique_choice: 1 },
  7: { token_youth: 5, token_fire: 3, token_wind: 3, token_forest: 3, token_mountain: 3 },
  8: { token_master_choice: 1, token_technique_choice: 2 },
  9: { token_fire: 5, token_wind: 5, token_forest: 5, token_mountain: 5, token_technique_choice: 2 },
  10: { token_master_choice: 2, token_technique_choice: 3, token_youth: 8 },
};

export type ClubProgressDelta = Partial<Record<ClubProgressMetric, number>> & {
  kickPlayerIds?: string[];
};

export type ClubProgressUpdate = {
  progress: ClubProgress;
  unlockedAchievements: ClubAchievement[];
  reachedLevels: number[];
  rewards: Reward;
};

export function createClubProgress(): ClubProgress {
  return {
    level: 1,
    xp: 0,
    completedAchievementIds: [],
    rewardedLevels: [],
    metrics: {
      wins: 0,
      recruits: 0,
      fireRecruits: 0,
      windRecruits: 0,
      forestRecruits: 0,
      mountainRecruits: 0,
      leagueTitles: 0,
      tournamentTitles: 0,
      kickImprovedPlayerIds: [],
    },
  };
}

export function normaliseClubProgress(value: unknown): ClubProgress {
  const base = createClubProgress();
  if (!value || typeof value !== 'object') return base;
  const raw = value as Partial<ClubProgress>;
  const metrics = raw.metrics && typeof raw.metrics === 'object' ? raw.metrics : {};
  const numberMetric = (key: ClubProgressMetric) => Math.max(0, Math.floor(Number((metrics as ClubProgress['metrics'])[key]) || 0));
  const completed = Array.isArray(raw.completedAchievementIds)
    ? [...new Set(raw.completedAchievementIds.filter((id): id is string => clubAchievements.some((achievement) => achievement.id === id)))]
    : [];
  const knownXp = clubAchievements.filter((achievement) => completed.includes(achievement.id)).reduce((sum, achievement) => sum + achievement.xp, 0);
  const xp = Math.max(0, Math.min(knownXp, Math.floor(Number(raw.xp) || knownXp)));
  const level = levelForXp(xp);
  return {
    level,
    xp,
    completedAchievementIds: completed,
    rewardedLevels: Array.isArray(raw.rewardedLevels)
      ? [...new Set(raw.rewardedLevels.filter((item): item is number => Number.isInteger(item) && item >= 2 && item <= level))]
      : [],
    metrics: {
      wins: numberMetric('wins'), recruits: numberMetric('recruits'), fireRecruits: numberMetric('fireRecruits'), windRecruits: numberMetric('windRecruits'),
      forestRecruits: numberMetric('forestRecruits'), mountainRecruits: numberMetric('mountainRecruits'), leagueTitles: numberMetric('leagueTitles'), tournamentTitles: numberMetric('tournamentTitles'),
      kickImprovedPlayerIds: Array.isArray((metrics as ClubProgress['metrics']).kickImprovedPlayerIds)
        ? [...new Set((metrics as ClubProgress['metrics']).kickImprovedPlayerIds.filter((id): id is string => typeof id === 'string'))]
        : [],
    },
  };
}

export function clubAchievementValue(progress: ClubProgress, achievement: ClubAchievement): number {
  return achievement.metric === 'kickImprovedPlayers'
    ? progress.metrics.kickImprovedPlayerIds.length
    : progress.metrics[achievement.metric];
}

export function levelForXp(xp: number): number {
  let level = 1;
  CLUB_LEVEL_XP.forEach((threshold, index) => { if (xp >= threshold) level = index + 1; });
  return Math.min(CLUB_LEVEL_MAX, level);
}

export function nextClubLevelXp(progress: ClubProgress): number | null {
  return progress.level >= CLUB_LEVEL_MAX ? null : CLUB_LEVEL_XP[progress.level];
}

export function advanceClubProgress(previous: ClubProgress | undefined, delta: ClubProgressDelta): ClubProgressUpdate {
  const base = normaliseClubProgress(previous);
  const metricChanges = Object.fromEntries((Object.keys(base.metrics) as Array<keyof ClubProgress['metrics']>)
    .filter((key) => key !== 'kickImprovedPlayerIds')
    .map((key) => [key, base.metrics[key as ClubProgressMetric]])) as Record<ClubProgressMetric, number>;
  (Object.keys(metricChanges) as ClubProgressMetric[]).forEach((key) => {
    metricChanges[key] = Math.max(0, metricChanges[key] + Math.floor(delta[key] ?? 0));
  });
  const kickImprovedPlayerIds = [...new Set([...base.metrics.kickImprovedPlayerIds, ...(delta.kickPlayerIds ?? [])])];
  const candidate: ClubProgress = { ...base, metrics: { ...metricChanges, kickImprovedPlayerIds } };
  const unlockedAchievements = clubAchievements.filter((achievement) => !base.completedAchievementIds.includes(achievement.id)
    && clubAchievementValue(candidate, achievement) >= achievement.target);
  const completedAchievementIds = [...base.completedAchievementIds, ...unlockedAchievements.map((achievement) => achievement.id)];
  const xp = completedAchievementIds.reduce((sum, id) => sum + (clubAchievements.find((achievement) => achievement.id === id)?.xp ?? 0), 0);
  const level = levelForXp(xp);
  const reachedLevels = Array.from({ length: Math.max(0, level - base.level) }, (_, index) => base.level + index + 1);
  const rewardedLevels = [...new Set([...base.rewardedLevels, ...reachedLevels])];
  const rewards = reachedLevels.reduce<Reward>((total, reachedLevel) => {
    for (const [tokenId, amount] of Object.entries(clubLevelRewards[reachedLevel] ?? {})) total[tokenId] = (total[tokenId] ?? 0) + amount;
    return total;
  }, {});
  return { progress: { level, xp, completedAchievementIds, rewardedLevels, metrics: candidate.metrics }, unlockedAchievements, reachedLevels, rewards };
}
